import { addDays } from 'date-fns';
import { formatDateISO } from '@elka/utils';
import type { ActivityInput, CallConnection } from '@elka/types';
import { buildCallTitle } from './callActivity';
import type { FollowUpData } from './callActivity';

/** The composer's call outcomes, mapped onto the closest existing CallConnection. */
export const OUTCOME_OPTIONS: { value: CallConnection; label: string }[] = [
  { value: 'answered', label: 'ענה' },
  { value: 'no_answer', label: 'לא ענה' },
  { value: 'voicemail', label: 'השארתי הודעה' },
  { value: 'busy', label: 'ביקש לחזור' },
];

/** Follow-up chips: days from today, null = no follow-up. */
export const FOLLOW_UP_OPTIONS: { days: number | null; label: string }[] = [
  { days: null, label: 'ללא' },
  { days: 1, label: 'מחר' },
  { days: 3, label: 'בעוד 3 ימים' },
  { days: 7, label: 'בעוד שבוע' },
];

/** Local YYYY-MM-DD, `days` from today. */
export function daysFromTodayISO(days: number): string {
  return formatDateISO(addDays(new Date(), days));
}

/** What an activity or task logged from the composer / quick-log links to. */
export interface LogTarget {
  contactId?: string;
  contactName?: string;
  companyId?: string;
  companyName?: string;
  dealId?: string;
  dealName?: string;
}

export interface Performer {
  uid: string;
  name: string;
}

/**
 * The ActivityInput for a call, meeting or note. Calls are titled with the chip the
 * user picked ("שיחה עם X · ביקש לחזור"), not the CallConnection label it maps to;
 * meetings keep useLogActivity's auto-title; notes are just "הערה".
 */
export function buildLogInput(
  type: 'call' | 'meeting' | 'note',
  target: LogTarget,
  text: string,
  performer: Performer,
  connection?: CallConnection
): ActivityInput {
  let title = '';
  if (type === 'note') title = 'הערה';
  if (type === 'call') {
    const outcome = OUTCOME_OPTIONS.find((o) => o.value === connection)?.label;
    const base = buildCallTitle({
      type,
      contactName: target.contactName,
      companyName: target.companyName,
    });
    title = outcome ? `${base} · ${outcome}` : base;
  }
  return {
    ...target,
    type,
    title,
    date: formatDateISO(new Date()),
    description: text.trim() || undefined,
    ...(type === 'call' ? { callConnection: connection, callDirection: 'outbound' as const } : {}),
    performedById: performer.uid,
    performedByName: performer.name,
  };
}

/** useLogActivity's follow-up option for a chip value, assigned to the performer. */
export function followUpOption(
  days: number | null,
  performer: Performer
): { followUp: FollowUpData } | undefined {
  if (days === null) return undefined;
  return {
    followUp: {
      date: daysFromTodayISO(days),
      assigneeId: performer.uid,
      assigneeName: performer.name,
    },
  };
}

/** The success toast after a log: "השיחה נרשמה" (+ " · נוספה משימת מעקב"). */
export function loggedMessage(type: 'call' | 'meeting' | 'note', withFollowUp: boolean): string {
  const base = { call: 'השיחה נרשמה', meeting: 'הפגישה נרשמה', note: 'ההערה נשמרה' }[type];
  return withFollowUp ? `${base} · נוספה משימת מעקב` : base;
}
